import { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Badge,
  Button,
  Banner,
  Spinner,
  Divider,
  DataTable,
  TextField,
  Checkbox,
  EmptyState
} from '@shopify/polaris';
import {
  InfoIcon,
  RefreshIcon
} from '@shopify/polaris-icons';

const CONFIDENCE_TONE = { high: 'success', medium: 'warning', low: 'critical' };

function MetricTile({ label, value, sub, critical }) {
  return (
    <div style={{ flex: 1, minWidth: 140, padding: '12px 16px', background: critical ? '#fff5f5' : '#f6f6f7', borderRadius: 8 }}>
      <Text variant="bodySm" tone="subdued">{label}</Text>
      <Text variant="headingLg" tone={critical ? 'critical' : undefined}>{value}</Text>
      {sub && <Text variant="bodySm" tone="subdued">{sub}</Text>}
    </div>
  );
}

function AlertRow({ alert, onDismiss }) {
  const severityTone = { high: 'critical', medium: 'caution', low: 'info' };
  const tone = severityTone[alert.severity] || 'info';
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 0', borderBottom: '1px solid #e1e3e5' }}>
      <Badge tone={tone}>{alert.severity}</Badge>
      <div style={{ flex: 1 }}>
        <Text variant="bodyMd">{alert.message}</Text>
        <Text variant="bodySm" tone="subdued">
          {alert.alert_type.replace(/_/g, ' ')}
          {alert.order_number ? ` · ${alert.order_number}` : ''}
          {alert.product_title ? ` · ${alert.product_title}` : ''}
          {' · '}{new Date(alert.created_at).toLocaleDateString()}
        </Text>
      </div>
      <Button size="slim" variant="plain" onClick={() => onDismiss(alert.id)}>Dismiss</Button>
    </div>
  );
}

export default function Insights({ shop, shopData, showToast }) {
  const [digest, setDigest] = useState(null);
  const [digests, setDigests] = useState([]);
  const [repricing, setRepricing] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [insightSettings, setInsightSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [repricingLoading, setRepricingLoading] = useState(false);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('digest');

  const currency = shopData?.settings?.currency || 'USD';
  const fmt = useCallback((v) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency, maximumFractionDigits: 0
    }).format(parseFloat(v) || 0);
  }, [currency]);

  const loadDigest = useCallback(async () => {
    if (!shop) return;
    try {
      const r = await fetch(`/api/insights/digest?shop=${shop}`);
      if (r.ok) {
        const d = await r.json();
        setDigest(d.digest);
      }
    } catch (err) {
      console.error('Digest load error:', err);
    }
  }, [shop]);

  const loadHistory = useCallback(async () => {
    if (!shop) return;
    try {
      const r = await fetch(`/api/insights/digests?shop=${shop}`);
      if (r.ok) {
        const d = await r.json();
        setDigests(d.reports || []);
      }
    } catch (err) {
      console.error('History load error:', err);
    }
  }, [shop]);

  const loadRepricing = useCallback(async () => {
    if (!shop) return;
    setRepricingLoading(true);
    try {
      const r = await fetch(`/api/insights/repricing?shop=${shop}`);
      if (r.ok) {
        const d = await r.json();
        setRepricing(d.recommendations || []);
      }
    } catch (err) {
      console.error('Repricing load error:', err);
    } finally {
      setRepricingLoading(false);
    }
  }, [shop]);

  const loadAlerts = useCallback(async () => {
    if (!shop) return;
    setAlertsLoading(true);
    try {
      const r = await fetch(`/api/insights/alerts?shop=${shop}`);
      if (r.ok) {
        const d = await r.json();
        setAlerts(d.alerts || []);
      }
    } catch (err) {
      console.error('Alerts load error:', err);
    } finally {
      setAlertsLoading(false);
    }
  }, [shop]);

  const loadSettings = useCallback(async () => {
    if (!shop) return;
    try {
      const r = await fetch(`/api/insights/settings?shop=${shop}`);
      if (r.ok) {
        const d = await r.json();
        setInsightSettings(d);
      }
    } catch (err) {
      console.error('Insight settings load error:', err);
    }
  }, [shop]);

  useEffect(() => {
    if (!shop) { setLoading(false); return; }
    Promise.all([loadDigest(), loadHistory(), loadAlerts(), loadSettings()])
      .finally(() => setLoading(false));
  }, [shop, loadDigest, loadHistory, loadAlerts, loadSettings]);

  useEffect(() => {
    if (activeSection === 'repricing' && repricing.length === 0 && !repricingLoading) {
      loadRepricing();
    }
  }, [activeSection, repricing.length, repricingLoading, loadRepricing]);

  const handleDismissAlert = async (alertId) => {
    try {
      await fetch(`/api/insights/alerts/${alertId}/dismiss`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop })
      });
      setAlerts(prev => prev.filter(a => a.id !== alertId));
      showToast('Alert dismissed');
    } catch (err) {
      showToast('Failed to dismiss alert', true);
    }
  };

  const handleActionRepricing = async (id) => {
    try {
      await fetch(`/api/insights/repricing/${id}/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop })
      });
      setRepricing(prev => prev.map(r => r.id === id ? { ...r, is_actioned: true } : r));
      showToast('Marked as actioned');
    } catch (err) {
      showToast('Failed to update', true);
    }
  };

  const handleSaveSettings = async () => {
    if (!insightSettings) return;
    setSettingsSaving(true);
    try {
      const r = await fetch('/api/insights/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop, ...insightSettings })
      });
      if (r.ok) {
        showToast('Insight settings saved');
      } else {
        showToast('Failed to save settings', true);
      }
    } catch (err) {
      showToast('Failed to save settings', true);
    } finally {
      setSettingsSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setInsightSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <Page title="Profit Insights">
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <Spinner size="large" />
        </div>
      </Page>
    );
  }

  // ---- Section nav ----
  const sections = [
    { id: 'digest', label: 'Daily Digest' },
    { id: 'alerts', label: `Alerts${alerts.length > 0 ? ` (${alerts.length})` : ''}` },
    { id: 'repricing', label: 'Repricing' },
    { id: 'history', label: 'History' },
    { id: 'settings', label: 'Settings' }
  ];

  return (
    <Page
      title="Profit Insights"
      subtitle="Automated profit monitoring, alerts, and repricing recommendations"
      primaryAction={{
        content: 'Refresh Digest',
        icon: RefreshIcon,
        onAction: () => { loadDigest(); loadAlerts(); showToast('Refreshed'); }
      }}
    >
      <BlockStack gap="400">
        {/* Section tabs (simple button row) */}
        <Card>
          <InlineStack gap="200" wrap={false}>
            {sections.map(s => (
              <Button
                key={s.id}
                variant={activeSection === s.id ? 'primary' : 'plain'}
                size="slim"
                onClick={() => setActiveSection(s.id)}
              >
                {s.label}
              </Button>
            ))}
          </InlineStack>
        </Card>

        {/* ---- DAILY DIGEST ---- */}
        {activeSection === 'digest' && (
          <BlockStack gap="400">
            {!digest ? (
              <Card>
                <EmptyState
                  heading="No digest available yet"
                  image=""
                >
                  <p>The daily digest runs automatically at 6 AM UTC. Check back tomorrow, or make sure your orders have been synced.</p>
                </EmptyState>
              </Card>
            ) : (
              <>
                <Card>
                  <BlockStack gap="300">
                    <InlineStack align="space-between">
                      <Text variant="headingMd">
                        Digest for {new Date(digest.report_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                      </Text>
                      {digest.total_orders === 0 && (
                        <Badge tone="info">No orders that day</Badge>
                      )}
                    </InlineStack>

                    {/* Key metrics */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                      <MetricTile label="Revenue" value={fmt(digest.total_revenue)} sub={`${digest.total_orders} orders`} />
                      <MetricTile
                        label="Net Profit"
                        value={fmt(digest.total_profit)}
                        critical={parseFloat(digest.total_profit) < 0}
                      />
                      <MetricTile
                        label="Avg Margin"
                        value={`${parseFloat(digest.avg_margin_percent).toFixed(1)}%`}
                        critical={parseFloat(digest.avg_margin_percent) < 0}
                      />
                      <MetricTile
                        label="Negative-Margin Orders"
                        value={digest.negative_margin_orders}
                        sub={digest.negative_margin_orders > 0 ? `${fmt(digest.negative_margin_loss)} lost` : 'none'}
                        critical={digest.negative_margin_orders > 0}
                      />
                      <MetricTile label="Low-Margin Orders" value={digest.low_margin_orders} />
                      <MetricTile label="Discount Leakage" value={fmt(digest.discount_leakage)} />
                      <MetricTile label="Shipping Drag" value={fmt(digest.shipping_drag)} />
                    </div>
                  </BlockStack>
                </Card>

                {/* Recommended actions */}
                {digest.recommendations && digest.recommendations.length > 0 && (
                  <Card>
                    <BlockStack gap="300">
                      <Text variant="headingMd">Recommended Actions</Text>
                      <BlockStack gap="200">
                        {digest.recommendations.map((rec, i) => (
                          <InlineStack key={i} gap="200" align="start">
                            <Badge tone={rec.impact === 'high' ? 'critical' : 'caution'}>{rec.impact}</Badge>
                            <Text variant="bodyMd">{rec.action}</Text>
                          </InlineStack>
                        ))}
                      </BlockStack>
                    </BlockStack>
                  </Card>
                )}

                {/* Low-margin products from raw_data */}
                {digest.raw_data?.low_margin_products?.length > 0 && (
                  <Card>
                    <BlockStack gap="300">
                      <Text variant="headingMd">Low-Margin Products</Text>
                      <DataTable
                        columnContentTypes={['text', 'numeric', 'numeric', 'numeric']}
                        headings={['Product', 'Price', 'Cost', 'Margin']}
                        rows={digest.raw_data.low_margin_products.map(p => [
                          p.title || '—',
                          fmt(p.price),
                          p.cost ? fmt(p.cost) : <Badge tone="warning">Missing</Badge>,
                          <Text tone={parseFloat(p.margin) < 0 ? 'critical' : 'caution'}>{p.margin}%</Text>
                        ])}
                      />
                    </BlockStack>
                  </Card>
                )}
              </>
            )}
          </BlockStack>
        )}

        {/* ---- ALERTS ---- */}
        {activeSection === 'alerts' && (
          <Card>
            <BlockStack gap="300">
              <InlineStack align="space-between">
                <Text variant="headingMd">Active Alerts</Text>
                <Button size="slim" icon={RefreshIcon} variant="plain" onClick={loadAlerts}>Refresh</Button>
              </InlineStack>

              {alertsLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><Spinner /></div>
              ) : alerts.length === 0 ? (
                <Banner tone="success">No active alerts — your margins are looking healthy.</Banner>
              ) : (
                <BlockStack gap="0">
                  {alerts.map(alert => (
                    <AlertRow key={alert.id} alert={alert} onDismiss={handleDismissAlert} />
                  ))}
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        )}

        {/* ---- REPRICING ---- */}
        {activeSection === 'repricing' && (
          <Card>
            <BlockStack gap="300">
              <InlineStack align="space-between">
                <Text variant="headingMd">Repricing Recommendations</Text>
                <Button size="slim" icon={RefreshIcon} variant="plain" onClick={loadRepricing}>Refresh</Button>
              </InlineStack>

              <Banner tone="info">
                Recommendations update every 7 days. Products missing COGS data show low-confidence estimates.
              </Banner>

              {repricingLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><Spinner /></div>
              ) : repricing.length === 0 ? (
                <EmptyState heading="No repricing recommendations" image="">
                  <p>All products are currently at or above your margin threshold, or there is not enough order history to analyze.</p>
                </EmptyState>
              ) : (
                <BlockStack gap="300">
                  {repricing.map((rec) => (
                    <div
                      key={rec.id || `${rec.shopify_variant_id}`}
                      style={{
                        padding: '12px 16px',
                        border: '1px solid #e1e3e5',
                        borderRadius: 8,
                        opacity: rec.is_actioned ? 0.5 : 1
                      }}
                    >
                      <InlineStack align="space-between" wrap={false}>
                        <BlockStack gap="100">
                          <InlineStack gap="200">
                            <Text variant="headingSm">{rec.title || 'Unnamed product'}</Text>
                            <Badge tone={CONFIDENCE_TONE[rec.confidence] || 'info'}>
                              {rec.confidence} confidence
                            </Badge>
                            {rec.is_actioned && <Badge tone="success">Actioned</Badge>}
                          </InlineStack>
                          <Text variant="bodySm" tone="subdued">{rec.reason}</Text>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 6 }}>
                            <span>
                              <Text variant="bodySm" tone="subdued">Current price </Text>
                              <Text variant="bodySm">{fmt(rec.current_price)}</Text>
                            </span>
                            <span>
                              <Text variant="bodySm" tone="subdued">Est. cost </Text>
                              <Text variant="bodySm">
                                {rec.estimated_cost ? fmt(rec.estimated_cost) : <Badge tone="warning">Missing</Badge>}
                              </Text>
                            </span>
                            <span>
                              <Text variant="bodySm" tone="subdued">Current margin </Text>
                              <Text variant="bodySm" tone={parseFloat(rec.current_margin_percent) < 0 ? 'critical' : 'caution'}>
                                {parseFloat(rec.current_margin_percent).toFixed(1)}%
                              </Text>
                            </span>
                            {rec.recommended_min_price && (
                              <span>
                                <Text variant="bodySm" tone="subdued">Min profitable price </Text>
                                <Text variant="bodySm" tone="success">{fmt(rec.recommended_min_price)}</Text>
                              </span>
                            )}
                            <span>
                              <Text variant="bodySm" tone="subdued">Orders (30d) </Text>
                              <Text variant="bodySm">{rec.order_count_30d}</Text>
                            </span>
                          </div>
                        </BlockStack>
                        {!rec.is_actioned && (
                          <Button
                            size="slim"
                            variant="secondary"
                            onClick={() => handleActionRepricing(rec.id)}
                          >
                            Mark actioned
                          </Button>
                        )}
                      </InlineStack>
                    </div>
                  ))}
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        )}

        {/* ---- HISTORY ---- */}
        {activeSection === 'history' && (
          <Card>
            <BlockStack gap="300">
              <Text variant="headingMd">Daily Report History (last 30 days)</Text>

              {digests.length === 0 ? (
                <EmptyState heading="No historical reports yet" image="">
                  <p>Reports appear here after the daily cron runs (6 AM UTC). You can also trigger a digest manually from the cron endpoint.</p>
                </EmptyState>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric']}
                  headings={['Date', 'Orders', 'Revenue', 'Profit', 'Margin', '⚠ Negative', 'Discounts']}
                  rows={digests.map(r => [
                    new Date(r.report_date + 'T00:00:00').toLocaleDateString(),
                    r.total_orders,
                    fmt(r.total_revenue),
                    <Text tone={parseFloat(r.total_profit) < 0 ? 'critical' : undefined}>{fmt(r.total_profit)}</Text>,
                    `${parseFloat(r.avg_margin_percent).toFixed(1)}%`,
                    r.negative_margin_orders > 0
                      ? <Badge tone="critical">{r.negative_margin_orders}</Badge>
                      : '0',
                    fmt(r.discount_leakage)
                  ])}
                />
              )}
            </BlockStack>
          </Card>
        )}

        {/* ---- SETTINGS ---- */}
        {activeSection === 'settings' && insightSettings && (
          <BlockStack gap="400">
            <Card>
              <BlockStack gap="400">
                <Text variant="headingMd">Digest & Alert Settings</Text>

                <BlockStack gap="300">
                  <Checkbox
                    label="Enable daily digest (runs at 6 AM UTC)"
                    checked={!!insightSettings.daily_digest_enabled}
                    onChange={v => updateSetting('daily_digest_enabled', v)}
                  />
                  <Checkbox
                    label="Enable weekly summary"
                    checked={!!insightSettings.weekly_summary_enabled}
                    onChange={v => updateSetting('weekly_summary_enabled', v)}
                  />
                  <Checkbox
                    label="Email daily digest"
                    checked={!!insightSettings.digest_email}
                    onChange={v => updateSetting('digest_email', v)}
                    helpText="Requires RESEND_API_KEY environment variable to be set on Vercel."
                  />
                  {insightSettings.digest_email && (
                    <TextField
                      label="Alert email recipient"
                      type="email"
                      value={insightSettings.alert_email_recipient || ''}
                      onChange={v => updateSetting('alert_email_recipient', v)}
                      placeholder="owner@yourstore.com"
                      autoComplete="email"
                    />
                  )}
                </BlockStack>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <Text variant="headingMd">Profit Thresholds</Text>

                <BlockStack gap="300">
                  <TextField
                    label="Target gross margin %"
                    type="number"
                    min="0"
                    max="100"
                    value={String(insightSettings.profit_target_percent ?? 25)}
                    onChange={v => updateSetting('profit_target_percent', parseFloat(v))}
                    suffix="%"
                    helpText="Repricing recommendations are generated for products below this target."
                    autoComplete="off"
                  />
                  <TextField
                    label="Low-margin threshold %"
                    type="number"
                    min="0"
                    max="100"
                    value={String(insightSettings.alert_margin_threshold ?? 20)}
                    onChange={v => updateSetting('alert_margin_threshold', parseFloat(v))}
                    suffix="%"
                    helpText="Alerts fire when a product or order margin falls below this."
                    autoComplete="off"
                  />
                  <TextField
                    label="Discount buffer %"
                    type="number"
                    min="0"
                    max="100"
                    value={String(insightSettings.discount_margin_buffer_percent ?? 10)}
                    onChange={v => updateSetting('discount_margin_buffer_percent', parseFloat(v))}
                    suffix="%"
                    helpText="Alert fires when discount % of revenue exceeds this."
                    autoComplete="off"
                  />
                  <TextField
                    label="Shipping alert threshold %"
                    type="number"
                    min="0"
                    max="100"
                    value={String(insightSettings.shipping_cost_alert_percent ?? 12)}
                    onChange={v => updateSetting('shipping_cost_alert_percent', parseFloat(v))}
                    suffix="%"
                    helpText="Alert fires when shipping % of revenue exceeds this."
                    autoComplete="off"
                  />
                  <TextField
                    label="Daily profit target (optional)"
                    type="number"
                    min="0"
                    value={insightSettings.daily_profit_target != null ? String(insightSettings.daily_profit_target) : ''}
                    onChange={v => updateSetting('daily_profit_target', v === '' ? null : parseFloat(v))}
                    prefix={shopData?.settings?.currency === 'USD' ? '$' : ''}
                    placeholder="Leave blank to disable"
                    helpText="Alert fires when net profit for the day falls below this amount."
                    autoComplete="off"
                  />
                </BlockStack>
              </BlockStack>
            </Card>

            <InlineStack align="end">
              <Button variant="primary" loading={settingsSaving} onClick={handleSaveSettings}>
                Save insight settings
              </Button>
            </InlineStack>
          </BlockStack>
        )}
      </BlockStack>
    </Page>
  );
}
